import { useAuth } from './use-auth';
import type { UpdateUserRequest, UploadPhotoResponse } from '@/api/endpoints/users';
import { updateUserProfile, uploadProfilePhoto } from '@/api/endpoints/users';
import { User } from '@/types/user';
import { currentUserQueryOptions, PROFILE_QUERY_KEYS, userByUsernameQueryOptions, userQueryOptions, type ProfileData } from '@/api/queries/users';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export { PROFILE_QUERY_KEYS } from '@/api/queries/users';

/**
 * Hook to fetch current user's profile data
 * Uses domain-oriented API module
 */
export function useCurrentUserProfile() {
  const { user: authUser } = useAuth();
  const userId = authUser?.id;

  const queryOptions = currentUserQueryOptions(userId || '');

  return useQuery({
    ...queryOptions,
    enabled: !!userId, // Only run query if we have a user ID
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error && 'status' in error && (error as any).status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook to fetch any user's profile data by ID
 * Uses domain-oriented API module
 */
export function useUserProfile(userId: string) {
  return useQuery(userQueryOptions(userId));
}

/**
 * Hook to fetch any user's profile data by username
 * Uses domain-oriented API module
 */
export function useUserProfileByUsername(username: string) {
  return useQuery(userByUsernameQueryOptions(username));
}

// Re-export types from API module for backward compatibility
export type { UploadPhotoResponse as PhotoUploadResponse, UpdateUserRequest as UpdateProfileData } from '@/api/endpoints/users';

/**
 * Hook to update current user's profile
 * Uses domain-oriented API module
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateData: UpdateUserRequest): Promise<User> => {
      return updateUserProfile(updateData);
    },
    onSuccess: (updatedUser) => {
      // Update the cache with the new profile data
      const profileData: ProfileData = { user: updatedUser };

      queryClient.setQueryData(PROFILE_QUERY_KEYS.currentUser(), profileData);

      // Invalidate related queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: PROFILE_QUERY_KEYS.all,
      });
    },
    onError: (error) => {
      console.error('Failed to update profile:', error);
    },
  });
}

/**
 * Hook to prefetch profile data (useful for app initialization)
 * Uses domain-oriented query options
 */
export function usePrefetchProfile() {
  const queryClient = useQueryClient();

  const prefetchCurrentUser = (userId: string) => {
    return queryClient.prefetchQuery(currentUserQueryOptions(userId));
  };

  const prefetchUser = (userId: string) => {
    return queryClient.prefetchQuery(userQueryOptions(userId));
  };

  return {
    prefetchCurrentUser,
    prefetchUser,
  };
}

/**
 * Hook to get cached profile data without triggering a network request
 */
export function useCachedProfile(userId?: string) {
  const queryClient = useQueryClient();

  const getCurrentUserProfile = () => {
    return queryClient.getQueryData<ProfileData>(PROFILE_QUERY_KEYS.currentUser());
  };

  const getUserProfile = (id: string) => {
    return queryClient.getQueryData<ProfileData>(PROFILE_QUERY_KEYS.user(id));
  };

  if (userId) {
    return getUserProfile(userId);
  }

  return getCurrentUserProfile();
}

/**
 * Hook to invalidate profile queries (useful for logout)
 */
export function useInvalidateProfile() {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    return queryClient.invalidateQueries({
      queryKey: PROFILE_QUERY_KEYS.all,
    });
  };

  const invalidateCurrentUser = () => {
    return queryClient.invalidateQueries({
      queryKey: PROFILE_QUERY_KEYS.currentUser(),
    });
  };

  const invalidateUser = (userId: string) => {
    return queryClient.invalidateQueries({
      queryKey: PROFILE_QUERY_KEYS.user(userId),
    });
  };

  return {
    invalidateAll,
    invalidateCurrentUser,
    invalidateUser,
  };
}

/**
 * Hook to upload profile photo
 * Uses domain-oriented API module
 */
export function useUploadProfilePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (photoFile: { uri: string; type?: string; name?: string }): Promise<UploadPhotoResponse> => {
      return uploadProfilePhoto(photoFile);
    },
    onSuccess: (response) => {
      // Optimistically update the cached profile with the new image URL
      // This prevents UI from reverting to the old image during refetch
      const currentProfileData = queryClient.getQueryData<ProfileData>(PROFILE_QUERY_KEYS.currentUser());
      if (currentProfileData?.user) {
        queryClient.setQueryData<ProfileData>(PROFILE_QUERY_KEYS.currentUser(), {
          ...currentProfileData,
          user: {
            ...currentProfileData.user,
            image: response.file_url,
          },
        });
      }

      // Also update user-specific cache if we have the user ID
      if (currentProfileData?.user?.id) {
        queryClient.setQueryData<ProfileData>(PROFILE_QUERY_KEYS.user(currentProfileData.user.id), {
          ...currentProfileData,
          user: {
            ...currentProfileData.user,
            image: response.file_url,
          },
        });
      }

      // Invalidate profile queries to refresh the profile with new photo from server
      queryClient.invalidateQueries({
        queryKey: PROFILE_QUERY_KEYS.all,
      });
    },
    onError: (error) => {
      console.error('[useUploadProfilePhoto] Failed to upload photo:', error);
    },
  });
}
